import { useFetch } from '@/composables/useFetch';
import { useToast } from '@/composables/useToast';
import { endpointsList } from '@/helpers';
import axios from 'axios';
import { IAdUserInternalFormat } from 'types';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

export type WorkspaceParticipantType = 'owner' | 'reviewer';

const participantConfig = {
  owner: {
    addSuccessKey: 'workspaceOwnersManagement.ownerAdded',
    addErrorKey: 'workspaceOwnersManagement.errors.errorAddingOwner',
    alreadyAddedErrorKey: 'workspaceOwnersManagement.errors.ownerAlreadyAdded',
    getCandidatesErrorKey:
      'workspaceOwnersManagement.errors.errorGettingOwnerCandidates',
    getParticipantsErrorKey: 'workspaces.errors.errorGettingOwners',
    removeErrorKey: 'workspaceOwnersManagement.errors.errorRemovingOwner',
    removeSuccessKey: 'workspaceOwnersManagement.ownerRemoved',
    addParticipant: (workspaceId: string) =>
      endpointsList.workspaces.addWorkspaceOwner(workspaceId),
    getParticipants: (workspaceId: string) =>
      endpointsList.workspaces.getWorkspaceOwners(workspaceId),
    removeParticipant: (workspaceId: string, userOid: string) =>
      endpointsList.workspaces.removeWorkspaceOwner(workspaceId, userOid),
    searchParticipantCandidates: () =>
      endpointsList.workspaces.searchWorkspaceOwnerCandidates(),
  },
  reviewer: {
    addSuccessKey: 'workspaceReviewersManagement.reviewerAdded',
    addErrorKey: 'workspaceReviewersManagement.errors.errorAddingReviewer',
    alreadyAddedErrorKey:
      'workspaceReviewersManagement.errors.reviewerAlreadyAdded',
    getCandidatesErrorKey:
      'workspaceReviewersManagement.errors.errorGettingReviewerCandidates',
    getParticipantsErrorKey: 'workspaces.errors.errorGettingReviewers',
    removeErrorKey: 'workspaceReviewersManagement.errors.errorRemovingReviewer',
    removeSuccessKey: 'workspaceReviewersManagement.reviewerRemoved',
    addParticipant: (workspaceId: string) =>
      endpointsList.workspaces.addWorkspaceReviewer(workspaceId),
    getParticipants: (workspaceId: string) =>
      endpointsList.workspaces.getWorkspaceReviewers(workspaceId),
    removeParticipant: (workspaceId: string, userOid: string) =>
      endpointsList.workspaces.removeWorkspaceReviewer(workspaceId, userOid),
    searchParticipantCandidates: () =>
      endpointsList.workspaces.searchWorkspaceReviewerCandidates(),
  },
} as const;

export function useWorkspaceParticipants(
  participantType: WorkspaceParticipantType,
  workspaceId?: string,
) {
  const { t } = useI18n();
  const { createErrorToast, createSuccessToast } = useToast();
  const config = participantConfig[participantType];

  async function searchCandidates(
    query: string,
  ): Promise<IAdUserInternalFormat[]> {
    try {
      const response = await axios.get<IAdUserInternalFormat[]>(
        config.searchParticipantCandidates(),
        {
          params: { query },
        },
      );
      return response.data;
    } catch {
      createErrorToast(t(config.getCandidatesErrorKey));
      return [];
    }
  }

  const {
    data: workspaceParticipants,
    fetchData: fetchWorkspaceParticipants,
    isLoading: isLoadingWorkspaceParticipants,
  } = useFetch(getWorkspaceParticipants, undefined, false);

  async function getWorkspaceParticipants() {
    if (!workspaceId) {
      throw new Error(
        'workspaceId is required to fetch workspace participants',
      );
    }

    try {
      const response = await axios.get<IAdUserInternalFormat[]>(
        config.getParticipants(workspaceId),
      );
      return response.data;
    } catch {
      createErrorToast(t(config.getParticipantsErrorKey));
    }
  }

  const isAddingWorkspaceParticipant = ref(false);

  async function addParticipantToWorkspace(userOid: string) {
    if (!workspaceId) {
      throw new Error('workspaceId is required to add workspace participant');
    }

    try {
      isAddingWorkspaceParticipant.value = true;
      await axios.post(config.addParticipant(workspaceId), { userOid });
      createSuccessToast(t(config.addSuccessKey));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        createErrorToast(t(config.alreadyAddedErrorKey));
        return;
      }
      createErrorToast(t(config.addErrorKey));
    } finally {
      isAddingWorkspaceParticipant.value = false;
    }
  }

  async function removeParticipantFromWorkspace(userOid: string) {
    if (!workspaceId) {
      throw new Error(
        'workspaceId is required to remove workspace participant',
      );
    }

    try {
      await axios.delete(config.removeParticipant(workspaceId, userOid));
      createSuccessToast(t(config.removeSuccessKey));
    } catch {
      createErrorToast(t(config.removeErrorKey));
    }
  }

  return {
    searchCandidates,
    workspaceParticipants,
    fetchWorkspaceParticipants,
    isLoadingWorkspaceParticipants,
    isAddingWorkspaceParticipant,
    addParticipantToWorkspace,
    removeParticipantFromWorkspace,
  };
}
